/**
 * Best-effort format checking for US driver's licence numbers.
 *
 * This catches typing mistakes. It does not verify that a licence exists or belongs to anyone: there
 * is no public service that can do that, and "D1234567" is a perfectly well-formed California number
 * that is almost certainly nobody's licence. Treat a pass here as "plausible", never as "verified".
 *
 * States revise their formats from time to time, so a pattern here can go stale and start rejecting
 * genuine new licences. That is why every caller surfaces a failure as a warning rather than refusing
 * the value: recording an unusual but real licence matters more than catching every typo.
 *
 * Patterns are matched case-insensitively and ignore spaces and hyphens, since people write the same
 * number several ways.
 */
const LICENSE_PATTERNS = {
    AL: /^\d{1,8}$/,
    AK: /^\d{1,7}$/,
    AZ: /^([A-Z]\d{8}|[A-Z]{2}\d{2,5}|\d{9})$/,
    AR: /^\d{4,9}$/,
    CA: /^[A-Z]\d{7}$/,
    CO: /^(\d{9}|[A-Z]\d{3,6}|[A-Z]{2}\d{2,5})$/,
    CT: /^\d{9}$/,
    DE: /^\d{1,7}$/,
    DC: /^(\d{7}|\d{9})$/,
    FL: /^[A-Z]\d{12}$/,
    GA: /^\d{7,9}$/,
    HI: /^([A-Z]\d{8}|\d{9})$/,
    ID: /^([A-Z]{2}\d{6}[A-Z]|\d{9})$/,
    IL: /^[A-Z]\d{11,12}$/,
    IN: /^([A-Z]\d{9}|\d{9,10})$/,
    IA: /^(\d{9}|\d{3}[A-Z]{2}\d{4})$/,
    KS: /^([A-Z]\d[A-Z]\d[A-Z]|[A-Z]\d{8}|\d{9})$/,
    KY: /^([A-Z]\d{8}|\d{9})$/,
    LA: /^\d{1,9}$/,
    ME: /^(\d{7,8}|\d{7}[A-Z])$/,
    MD: /^[A-Z]\d{12}$/,
    MA: /^([A-Z]\d{8}|\d{9})$/,
    MI: /^[A-Z]\d{10,12}$/,
    MN: /^[A-Z]\d{12}$/,
    MS: /^\d{9}$/,
    MO: /^([A-Z]\d{5,9}|[A-Z]\d{6}R|\d{8}[A-Z]{2}|\d{9}[A-Z]|\d{9})$/,
    MT: /^([A-Z]\d{8}|\d{9}|\d{13,14})$/,
    NE: /^[A-Z]\d{6,8}$/,
    NV: /^(\d{9,10}|\d{12}|X\d{8})$/,
    NH: /^\d{2}[A-Z]{3}\d{5}$/,
    NJ: /^[A-Z]\d{14}$/,
    NM: /^\d{8,9}$/,
    NY: /^([A-Z]\d{7}|[A-Z]\d{18}|\d{8,9}|\d{16}|[A-Z]{8})$/,
    NC: /^\d{1,12}$/,
    ND: /^([A-Z]{3}\d{6}|\d{9})$/,
    OH: /^([A-Z]\d{4,8}|[A-Z]{2}\d{3,7}|\d{8})$/,
    OK: /^([A-Z]\d{9}|\d{9})$/,
    OR: /^\d{1,9}$/,
    PA: /^\d{8}$/,
    RI: /^(\d{7}|[A-Z]\d{6})$/,
    SC: /^\d{5,11}$/,
    SD: /^(\d{6,10}|\d{12})$/,
    TN: /^\d{7,9}$/,
    TX: /^\d{7,8}$/,
    UT: /^\d{4,10}$/,
    VT: /^(\d{8}|\d{7}A)$/,
    VA: /^([A-Z]\d{8,11}|\d{9})$/,
    // Washington builds the number from the holder's name, so it is alphanumeric with padding.
    WA: /^[A-Z0-9*]{7,12}$/,
    WV: /^(\d{7}|[A-Z]{1,2}\d{5,6})$/,
    WI: /^[A-Z]\d{13}$/,
    WY: /^\d{9,10}$/,
};

/** Plain-language descriptions, so a warning can say what the state expects. */
const LICENSE_DESCRIPTIONS = {
    CA: 'a letter followed by 7 digits',
    FL: 'a letter followed by 12 digits',
    MD: 'a letter followed by 12 digits',
    MN: 'a letter followed by 12 digits',
    NJ: 'a letter followed by 14 digits',
    WI: 'a letter followed by 13 digits',
    IL: 'a letter followed by 11 or 12 digits',
    NH: '2 digits, 3 letters, then 5 digits',
    PA: '8 digits',
    TX: '7 or 8 digits',
    CT: '9 digits',
    MS: '9 digits',
};

/**
 * Strips the punctuation people add and upper-cases, so the same number matches however it is typed.
 * @param licenseNumber {string|null|undefined}
 * @returns {string}
 */
export function normalizeLicenseNumber(licenseNumber) {
    return licenseNumber ? String(licenseNumber).replace(/[\s-]/g, '').toUpperCase() : '';
}

/** Whether a format is known for this state at all. */
export function hasLicensePattern(stateAbbreviation) {
    return Object.prototype.hasOwnProperty.call(LICENSE_PATTERNS, stateAbbreviation);
}

/**
 * Whether a licence number looks like one this state issues.
 *
 * Returns true when there is nothing to go on — no state chosen, no number entered, or no pattern
 * known for that state — because a warning is only useful when it is actually telling the person
 * something.
 * @param licenseNumber {string|null|undefined}
 * @param stateAbbreviation {string|null|undefined}
 * @returns {boolean}
 */
export function isPlausibleLicenseNumber(licenseNumber, stateAbbreviation) {
    const normalized = normalizeLicenseNumber(licenseNumber);

    if (!normalized || !stateAbbreviation || !hasLicensePattern(stateAbbreviation)) {
        return true;
    }

    return LICENSE_PATTERNS[stateAbbreviation].test(normalized);
}

/**
 * The warning to show under the licence field, or an empty string when there is nothing to say.
 * @param licenseNumber {string|null|undefined}
 * @param stateAbbreviation {string|null|undefined}
 * @returns {string}
 */
export function getLicenseWarning(licenseNumber, stateAbbreviation) {
    if (isPlausibleLicenseNumber(licenseNumber, stateAbbreviation)) {
        return '';
    }

    const expected = LICENSE_DESCRIPTIONS[stateAbbreviation];

    // Worded as a check rather than a rejection: the value is still saved either way, and a state can
    // change its format without this file knowing about it.
    return expected
        ? `This does not match the usual ${stateAbbreviation} format (${expected}). Please double-check it.`
        : `This does not match the usual ${stateAbbreviation} format. Please double-check it.`;
}
